import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useWorkspaceStore } from "@/hooks/use-workspace";
import { useToast } from "@/hooks/use-toast";
import {
  Send, Square, Clock, CheckCircle2, AlertCircle, Loader2,
  Monitor, FileText, Play, ChevronRight, X, RefreshCw, Cog
} from "lucide-react";
import ReactMarkdown from "react-markdown";

interface ClawSession {
  id: number;
  workspaceId: number;
  title: string;
  task: string;
  status: string;
  scheduleCron: string | null;
  result: string | null;
  createdAt: string;
  updatedAt: string;
}

interface ClawAction {
  id: number;
  sessionId: number;
  type: string;
  description: string;
  screenshotUrl: string | null;
  result: string | null;
  createdAt: string;
}

function StatusBadge({ status }: { status: string }) {
  const configs: Record<string, { label: string; className: string; icon: any }> = {
    pending: { label: "Pending", className: "bg-amber-500/15 text-amber-400 border-amber-500/20", icon: Clock },
    running: { label: "Running", className: "bg-blue-500/15 text-blue-400 border-blue-500/20", icon: Loader2 },
    done: { label: "Done", className: "bg-emerald-500/15 text-emerald-400 border-emerald-500/20", icon: CheckCircle2 },
    stopped: { label: "Stopped", className: "bg-muted/50 text-muted-foreground border-border", icon: Square },
    error: { label: "Error", className: "bg-red-500/15 text-red-400 border-red-500/20", icon: AlertCircle },
    failed: { label: "Failed", className: "bg-red-500/15 text-red-400 border-red-500/20", icon: AlertCircle },
  };
  const cfg = configs[status] || configs.pending;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full border ${cfg.className}`}>
      <Icon size={9} className={status === "running" ? "animate-spin" : ""} />
      {cfg.label}
    </span>
  );
}

function timeAgo(dateStr: string) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const m = Math.floor(diff / 60000);
  const h = Math.floor(m / 60);
  const d = Math.floor(h / 24);
  if (d > 0) return `${d}d ago`;
  if (h > 0) return `${h}h ago`;
  if (m > 0) return `${m}m ago`;
  return "just now";
}

function ActionTypeIcon({ type }: { type: string }) {
  switch (type) {
    case "navigate": return <span className="text-blue-400 text-[10px] font-mono">→</span>;
    case "click": return <span className="text-amber-400 text-[10px] font-mono">⬡</span>;
    case "type": return <span className="text-green-400 text-[10px] font-mono">⌨</span>;
    case "extract": return <span className="text-purple-400 text-[10px] font-mono">⊞</span>;
    case "milestone": return <CheckCircle2 size={11} className="text-emerald-400" />;
    case "error": return <AlertCircle size={11} className="text-red-400" />;
    case "start": return <Play size={11} className="text-blue-400" />;
    default: return <span className="text-muted-foreground/60 text-[10px]">•</span>;
  }
}

export default function ClawPage() {
  const { activeWorkspaceId } = useWorkspaceStore();
  const { toast } = useToast();
  const [task, setTask] = useState("");
  const [scheduleCron, setScheduleCron] = useState("");
  const [showSchedule, setShowSchedule] = useState(false);
  const [activeSessionId, setActiveSessionId] = useState<number | null>(null);
  const [liveActions, setLiveActions] = useState<ClawAction[]>([]);
  const [liveScreenshot, setLiveScreenshot] = useState<string | null>(null);
  const [liveDone, setLiveDone] = useState<string | null>(null);
  const [liveStatus, setLiveStatus] = useState<string>("pending");
  const eventSourceRef = useRef<EventSource | null>(null);
  const actionLogRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { data: sessions = [], isLoading: sessionsLoading } = useQuery<ClawSession[]>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "claw/sessions"],
    queryFn: async () => {
      if (!activeWorkspaceId) return [];
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}/claw/sessions`, { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!activeWorkspaceId,
    refetchInterval: activeSessionId ? 3000 : 10000,
  });

  const { data: sessionDetail } = useQuery<{ session: ClawSession; actions: ClawAction[] }>({
    queryKey: ["/api/claw/sessions", activeSessionId],
    queryFn: async () => {
      if (!activeSessionId) return null;
      const res = await fetch(`/api/claw/sessions/${activeSessionId}`, { credentials: "include" });
      if (!res.ok) return null;
      return res.json();
    },
    enabled: !!activeSessionId,
  });

  const startMutation = useMutation({
    mutationFn: async (data: { task: string; scheduleCron?: string }) => {
      const res = await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/claw/sessions`, data);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Failed to start task");
      }
      return res.json() as Promise<ClawSession>;
    },
    onSuccess: (session) => {
      setActiveSessionId(session.id);
      setLiveActions([]);
      setLiveScreenshot(null);
      setLiveDone(null);
      setLiveStatus("running");
      setTask("");
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "claw/sessions"] });
      toast({ title: "Claw is working", description: "Your task has been started." });
    },
    onError: (err: any) => {
      toast({ title: "Failed to start task", description: err.message, variant: "destructive" });
    },
  });

  const stopMutation = useMutation({
    mutationFn: async (sessionId: number) => {
      const res = await apiRequest("POST", `/api/claw/sessions/${sessionId}/stop`, {});
      if (!res.ok) throw new Error("Failed to stop");
      return res.json();
    },
    onSuccess: () => {
      setLiveStatus("stopped");
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "claw/sessions"] });
      toast({ title: "Task stopped" });
    },
  });

  useEffect(() => {
    if (!activeSessionId) return;
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    const es = new EventSource(`/api/claw/sessions/${activeSessionId}/stream`, { withCredentials: true } as any);
    eventSourceRef.current = es;

    es.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data.type === "init") {
          setLiveActions(data.actions || []);
          setLiveStatus(data.session?.status || "pending");
          if (data.session?.result) setLiveDone(data.session.result);
        } else if (data.type === "action") {
          const action: ClawAction = {
            id: Date.now(),
            sessionId: activeSessionId,
            type: data.actionType || "action",
            description: data.description || "",
            screenshotUrl: data.screenshotUrl || null,
            result: data.result || null,
            createdAt: data.timestamp || new Date().toISOString(),
          };
          setLiveActions(prev => [...prev, action]);
          queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "claw/sessions"] });
        } else if (data.type === "screenshot") {
          setLiveScreenshot(data.dataUrl);
        } else if (data.type === "done") {
          setLiveDone(data.report || "");
          setLiveStatus(data.status || "done"); // "failed" / "stopped" end the run too, and must not read as Done
          queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "claw/sessions"] });
          queryClient.invalidateQueries({ queryKey: ["/api/claw/sessions", activeSessionId] });
        }
      } catch {}
    };

    es.onerror = () => {
      es.close();
    };

    return () => { es.close(); };
  }, [activeSessionId, activeWorkspaceId]);

  useEffect(() => {
    if (actionLogRef.current) {
      actionLogRef.current.scrollTop = actionLogRef.current.scrollHeight;
    }
  }, [liveActions]);

  const handleSubmit = () => {
    if (!task.trim() || startMutation.isPending) return;
    startMutation.mutate({
      task: task.trim(),
      scheduleCron: scheduleCron.trim() || undefined,
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const isRunning = liveStatus === "running" || liveStatus === "pending";
  const activeSession = sessions.find(s => s.id === activeSessionId);

  const displayActions = activeSessionId ? liveActions : (sessionDetail?.actions || []);
  const displayResult = activeSessionId ? liveDone : sessionDetail?.session?.result;
  const displayStatus = activeSessionId ? liveStatus : sessionDetail?.session?.status || "pending";

  return (
    <div className="flex h-full min-h-0 overflow-hidden bg-background">
      <div className="w-64 shrink-0 border-r border-border flex flex-col overflow-hidden">
        <div className="p-4 border-b border-border">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-purple-500/20 to-violet-600/20 border border-purple-500/30 flex items-center justify-center">
              <Monitor size={16} className="text-purple-400" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-foreground">Claw</h2>
              <p className="text-[10px] text-muted-foreground/70">Computer Use Agent</p>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto custom-scrollbar p-2">
          {sessionsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 size={16} className="animate-spin text-muted-foreground/50" />
            </div>
          ) : sessions.length === 0 ? (
            <div className="text-center py-8 px-3">
              <p className="text-[11px] text-muted-foreground/50">No past tasks yet.</p>
            </div>
          ) : (
            sessions.map(session => (
              <button
                key={session.id}
                data-testid={`session-item-${session.id}`}
                onClick={() => {
                  setActiveSessionId(session.id);
                  setLiveActions([]);
                  setLiveScreenshot(null);
                  setLiveDone(session.result);
                  setLiveStatus(session.status);
                }}
                className={`w-full text-left p-3 rounded-xl mb-1 transition-all border ${
                  activeSessionId === session.id
                    ? "bg-purple-500/[0.08] border-purple-500/20"
                    : "border-transparent hover:bg-muted/40"
                }`}
              >
                <div className="flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-foreground/80 truncate">{session.title || session.task}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <StatusBadge status={session.status} />
                      <span className="text-[10px] text-muted-foreground/50">{timeAgo(session.createdAt)}</span>
                    </div>
                  </div>
                  {activeSessionId === session.id && <ChevronRight size={12} className="text-purple-400 shrink-0 mt-0.5" />}
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {!activeSessionId && !startMutation.isPending ? (
          <div className="flex-1 flex flex-col items-center justify-center px-8 py-16">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500/15 to-violet-600/15 border border-purple-500/25 flex items-center justify-center mb-6">
              <Monitor size={28} className="text-purple-400" />
            </div>
            <h1 className="text-2xl font-display font-bold text-foreground mb-2">Meet Claw</h1>
            <p className="text-sm text-muted-foreground text-center max-w-lg mb-8 leading-relaxed">
              Claw is your AI computer use agent. Give it any task and it will work through it by controlling a real browser — navigating websites, filling forms, extracting data, and reporting back.
            </p>
            <div className="w-full max-w-2xl space-y-3">
              <div className="relative">
                <textarea
                  ref={textareaRef}
                  data-testid="input-claw-task"
                  value={task}
                  onChange={e => setTask(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Describe what you want Claw to do...

Examples:
• Research these 5 companies and find the decision maker at each
• Go to this URL and extract all pricing information
• Search Google for recent news about [topic] and summarize findings"
                  rows={6}
                  className="w-full px-4 py-3.5 bg-muted/40 border border-border rounded-2xl text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-purple-500/50 resize-none transition-colors"
                />
              </div>

              <div className="flex items-center justify-between gap-3">
                <button
                  data-testid="button-toggle-schedule"
                  onClick={() => setShowSchedule(!showSchedule)}
                  className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border transition-all ${
                    showSchedule
                      ? "bg-purple-500/10 border-purple-500/30 text-purple-400"
                      : "border-border text-muted-foreground hover:border-border hover:text-foreground/70"
                  }`}
                >
                  <Cog size={12} />
                  Schedule
                </button>
                <button
                  data-testid="button-start-claw"
                  onClick={handleSubmit}
                  disabled={!task.trim() || startMutation.isPending}
                  className="flex items-center gap-2 px-6 py-2.5 bg-purple-600 hover:bg-purple-500 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-xl text-sm font-bold transition-all"
                >
                  {startMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
                  Run Task
                </button>
              </div>

              {showSchedule && (
                <div className="bg-muted/30 border border-border rounded-xl p-4 space-y-2" data-testid="schedule-panel">
                  <label className="text-[10px] font-bold text-muted-foreground/70 uppercase tracking-wider block">
                    Cron Schedule (optional)
                  </label>
                  <input
                    data-testid="input-cron-schedule"
                    value={scheduleCron}
                    onChange={e => setScheduleCron(e.target.value)}
                    placeholder="e.g. 0 9 * * * (every day at 9am)"
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm font-mono text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-purple-500/40"
                  />
                  <p className="text-[10px] text-muted-foreground/50">Use standard cron syntax. Leave blank for one-time run.</p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="flex-1 flex flex-col min-h-0">
            <div className="flex items-center justify-between px-5 py-3 border-b border-border">
              <div className="flex items-center gap-3">
                <StatusBadge status={displayStatus} />
                <span className="text-sm font-medium text-foreground/80 truncate max-w-[400px]">
                  {activeSession?.title || activeSession?.task || sessionDetail?.session?.title || ""}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {isRunning && activeSessionId && (
                  <button
                    data-testid="button-stop-claw"
                    onClick={() => stopMutation.mutate(activeSessionId)}
                    disabled={stopMutation.isPending}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-red-400 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 rounded-lg transition-all"
                  >
                    <Square size={11} />
                    Stop
                  </button>
                )}
                <button
                  data-testid="button-new-task"
                  onClick={() => {
                    setActiveSessionId(null);
                    setLiveActions([]);
                    setLiveScreenshot(null);
                    setLiveDone(null);
                    setLiveStatus("pending");
                  }}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground border border-border hover:border-border/80 rounded-lg transition-all"
                >
                  <RefreshCw size={11} />
                  New Task
                </button>
              </div>
            </div>

            <div className="flex-1 flex min-h-0 overflow-hidden">
              <div className="flex-1 flex flex-col min-h-0 border-r border-border">
                <div className="p-3 border-b border-border">
                  <div className="flex items-center gap-2">
                    <Monitor size={13} className="text-muted-foreground/60" />
                    <span className="text-[11px] font-bold text-muted-foreground/70 uppercase tracking-wider">Live Browser</span>
                    {isRunning && (
                      <span className="inline-flex items-center gap-1 text-[10px] text-blue-400">
                        <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />
                        Live
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex-1 flex items-center justify-center bg-black/20 p-4 overflow-hidden" data-testid="screenshot-panel">
                  {liveScreenshot ? (
                    <img
                      src={liveScreenshot}
                      alt="Browser screenshot"
                      className="max-w-full max-h-full object-contain rounded-lg border border-border/50 shadow-xl"
                      data-testid="browser-screenshot"
                    />
                  ) : (
                    <div className="text-center">
                      <Monitor size={32} className="text-muted-foreground/20 mx-auto mb-3" />
                      <p className="text-xs text-muted-foreground/40">
                        {isRunning ? "Waiting for browser..." : "No screenshot available"}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className="w-80 flex flex-col min-h-0 overflow-hidden">
                <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
                  <div className="shrink-0 p-3 border-b border-border">
                    <div className="flex items-center gap-2">
                      <FileText size={13} className="text-muted-foreground/60" />
                      <span className="text-[11px] font-bold text-muted-foreground/70 uppercase tracking-wider">Action Log</span>
                      {displayActions.length > 0 && (
                        <span className="text-[10px] text-muted-foreground/40 ml-auto">{displayActions.length} steps</span>
                      )}
                    </div>
                  </div>
                  <div ref={actionLogRef} className="flex-1 overflow-y-auto custom-scrollbar p-3 space-y-1.5" data-testid="action-log">
                    {displayResult && (
                      <div className="mb-3 p-3 rounded-xl bg-emerald-950/20 border border-emerald-500/20" data-testid="results-panel">
                        <div className="flex items-center gap-1.5 mb-2">
                          <CheckCircle2 size={11} className="text-emerald-400" />
                          <span className="text-[10px] font-bold text-emerald-400 uppercase tracking-wider">Final Report</span>
                        </div>
                        <div className="prose prose-sm prose-invert max-w-none text-foreground/70 text-xs leading-relaxed">
                          <ReactMarkdown>{displayResult}</ReactMarkdown>
                        </div>
                      </div>
                    )}
                    {displayActions.length === 0 && !displayResult ? (
                      <div className="flex items-center justify-center py-8">
                        {isRunning ? (
                          <div className="text-center">
                            <Loader2 size={20} className="animate-spin text-muted-foreground/30 mx-auto mb-2" />
                            <p className="text-[11px] text-muted-foreground/40">Claw is starting up...</p>
                          </div>
                        ) : (
                          <p className="text-[11px] text-muted-foreground/40">No actions recorded.</p>
                        )}
                      </div>
                    ) : (
                      displayActions.map((action, idx) => (
                        <div
                          key={action.id || idx}
                          data-testid={`action-log-item-${idx}`}
                          className={`flex items-start gap-2 p-2 rounded-lg text-xs ${
                            action.type === "error"
                              ? "bg-red-950/20 border border-red-500/15"
                              : action.type === "milestone"
                              ? "bg-emerald-950/20 border border-emerald-500/15"
                              : "bg-muted/20"
                          }`}
                        >
                          <div className="mt-0.5 shrink-0"><ActionTypeIcon type={action.type} /></div>
                          <p className={`flex-1 leading-snug ${
                            action.type === "error" ? "text-red-300/80" :
                            action.type === "milestone" ? "text-emerald-300/80" :
                            "text-foreground/60"
                          }`}>
                            {action.description}
                          </p>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>

            {!isRunning && !displayResult && (
              <div className="p-4 border-t border-border">
                <div className="flex gap-3 max-w-2xl mx-auto">
                  <textarea
                    data-testid="input-new-task-inline"
                    value={task}
                    onChange={e => setTask(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Type a new task for Claw..."
                    rows={2}
                    className="flex-1 px-3.5 py-2.5 bg-muted/40 border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-purple-500/50 resize-none"
                  />
                  <button
                    data-testid="button-run-new-task"
                    onClick={handleSubmit}
                    disabled={!task.trim() || startMutation.isPending}
                    className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-500 disabled:opacity-40 text-white rounded-xl text-sm font-bold transition-all self-end"
                  >
                    <Send size={13} />
                    Run
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
